import React, { useState, useEffect } from "react";
import { adminFetch } from "../../lib/api";
import type { AksCluster, AzureSubscription, MonitorEndpoint, MonitorType, ServiceRegionOption } from "../../types/monitoring";

interface ServiceItem { id: string; name: string; slug: string; description?: string; display_order: number; is_active: boolean; }
interface RegionItem { id: string; name: string; slug: string; display_order: number; }
interface ComponentItem { id: string; service_id: string; service_name: string; name: string; slug: string; display_order: number; is_active: boolean; }

type Tab = "services" | "regions" | "endpoints" | "subscriptions" | "clusters";

const METHOD_OPTIONS = ["GET", "HEAD", "POST"];

function MonitorTypeBadge({ type }: { type: MonitorType }) {
  return type === "aks_pod"
    ? <span className="ac-badge ac-badge--info">AKS Pod</span>
    : <span className="ac-badge ac-badge--neutral">HTTP</span>;
}

function toSlug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function ServiceRegistryPage() {
  const [tab, setTab] = useState<Tab>("services");
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [regions, setRegions] = useState<RegionItem[]>([]);
  const [components, setComponents] = useState<ComponentItem[]>([]);
  const [endpoints, setEndpoints] = useState<MonitorEndpoint[]>([]);
  const [serviceRegions, setServiceRegions] = useState<ServiceRegionOption[]>([]);
  const [subscriptions, setSubscriptions] = useState<AzureSubscription[]>([]);
  const [aksClusters, setAksClusters] = useState<AksCluster[]>([]);

  // Expanded service (show components inline)
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(null);

  // Service form
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [svcName, setSvcName] = useState("");
  const [svcSlug, setSvcSlug] = useState("");
  const [svcDesc, setSvcDesc] = useState("");
  const [svcOrder, setSvcOrder] = useState(0);

  // Inline order editing per service row
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editingOrderValue, setEditingOrderValue] = useState("");

  // Component form
  const [showComponentForm, setShowComponentForm] = useState(false);
  const [compServiceId, setCompServiceId] = useState("");
  const [compName, setCompName] = useState("");
  const [compSlug, setCompSlug] = useState("");
  const [compOrder, setCompOrder] = useState(0);
  const [compRegionIds, setCompRegionIds] = useState<string[]>([]);

  // Component edit dialog
  const [editCompId, setEditCompId] = useState<string | null>(null);
  const [editCompName, setEditCompName] = useState("");
  const [editCompSlug, setEditCompSlug] = useState("");
  const [editCompError, setEditCompError] = useState("");
  const [editCompSubmitting, setEditCompSubmitting] = useState(false);

  // Service clone dialog
  const [cloneSvcSourceId, setCloneSvcSourceId] = useState<string | null>(null);
  const [cloneSvcSourceName, setCloneSvcSourceName] = useState("");
  const [cloneSvcName, setCloneSvcName] = useState("");
  const [cloneSvcSlug, setCloneSvcSlug] = useState("");
  const [cloneSvcWithRegions, setCloneSvcWithRegions] = useState(false);
  const [cloneSvcError, setCloneSvcError] = useState("");
  const [cloneSvcSubmitting, setCloneSvcSubmitting] = useState(false);

  // Region form (add)
  const [showRegionForm, setShowRegionForm] = useState(false);
  const [regName, setRegName] = useState("");

  // Region edit
  const [editRegion, setEditRegion] = useState<RegionItem | null>(null);
  const [editRegName, setEditRegName] = useState("");
  const [editRegSlug, setEditRegSlug] = useState("");
  const [editRegOrder, setEditRegOrder] = useState(0);

  // Subscription form
  const [showSubForm, setShowSubForm] = useState(false);
  const [editSubId, setEditSubId] = useState<string | null>(null);
  const [subName, setSubName] = useState("");
  const [subSubscriptionId, setSubSubscriptionId] = useState("");
  const [subTenantId, setSubTenantId] = useState("");
  const [subClientId, setSubClientId] = useState("");
  const [subClientSecret, setSubClientSecret] = useState("");
  const [subSecretExpiresAt, setSubSecretExpiresAt] = useState("");
  const [subIsGov, setSubIsGov] = useState(false);

  // AKS Cluster form
  const [showClusterForm, setShowClusterForm] = useState(false);
  const [editClusterId, setEditClusterId] = useState<string | null>(null);
  const [clusterName, setClusterName] = useState("");
  const [clusterSubId, setClusterSubId] = useState("");

  // Inline "link regions to component" (multi-select)
  const [linkingComponentId, setLinkingComponentId] = useState<string | null>(null);
  const [linkCompRegionIds, setLinkCompRegionIds] = useState<string[]>([]);

  // Endpoint form (add + edit + clone)
  const [showEndpointForm, setShowEndpointForm] = useState(false);
  const [editEndpointId, setEditEndpointId] = useState<string | null>(null);
  const [isCloneMode, setIsCloneMode] = useState(false);
  // Per-row trigger state: id → "idle" | "running" | "ok" | "fail"
  const [triggerState, setTriggerState] = useState<Record<string, "idle" | "running" | "ok" | "fail">>({});
  const [epServiceRegion, setEpServiceRegion] = useState("");
  const [epMonitorType, setEpMonitorType] = useState<MonitorType>("http");
  const [epMonitorMode, setEpMonitorMode] = useState<"http" | "aks_pod" | "both">("http");
  const [epUrl, setEpUrl] = useState("");
  const [epMethod, setEpMethod] = useState("GET");
  const [epExpectedStatus, setEpExpectedStatus] = useState(200);
  const [epTimeout, setEpTimeout] = useState(10);
  const [epInterval, setEpInterval] = useState(60);
  const [epThreshold, setEpThreshold] = useState(3);
  const [epAksClusterId, setEpAksClusterId] = useState("");
  const [epAksSubFilter, setEpAksSubFilter] = useState("");
  const [epAksNamespace, setEpAksNamespace] = useState("default");

  // Endpoint response verification
  const [epExpectedBody, setEpExpectedBody] = useState("");
  const [epExpectedJsonPath, setEpExpectedJsonPath] = useState("");
  const [epExpectedJsonValue, setEpExpectedJsonValue] = useState("");

  // Healthcheck probe ("Check" button)
  type ProbeResult = {
    ok: boolean;
    status_ok: boolean;
    body_ok: boolean | null;
    json_ok: boolean | null;
    json_actual: string | null;
    json_error: string | null;
    http_status: number | null;
    response_ms: number | null;
    body_snippet: string | null;
    error: string | null;
  };
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);

  // AKS cluster check ("Check" button in AKS form)
  type AksProbeResult = {
    ok: boolean;
    status: string;
    pod_total: number | null;
    pod_ready: number | null;
    detail: string | null;
    error: string | null;
  };
  const [aksProbing, setAksProbing] = useState(false);
  const [aksProbeResult, setAksProbeResult] = useState<AksProbeResult | null>(null);

  const [error, setError] = useState("");

  // ── Filter state ──────────────────────────────────────────────────────────
  // Services
  const [svcFilter, setSvcFilter] = useState("");
  const [svcStatusFilter, setSvcStatusFilter] = useState<"all" | "active" | "inactive">("all");
  type SvcSortCol = "order" | "name" | "slug" | "status";
  const [svcSortCol, setSvcSortCol] = useState<SvcSortCol>("order");
  const [svcSortAsc, setSvcSortAsc] = useState(true);
  // Regions
  const [regFilter, setRegFilter] = useState("");
  // Endpoints
  const [epSearchFilter, setEpSearchFilter] = useState("");
  const [epTypeFilter, setEpTypeFilter] = useState<"all" | "http" | "aks_pod">("all");
  const [epStatusFilter, setEpStatusFilter] = useState<"all" | "operational" | "degraded" | "outage" | "maintenance" | "inactive">("all");
  const [epRegionFilter, setEpRegionFilter] = useState("");
  type EpSortCol = "service" | "component_region" | "type" | "target" | "interval" | "status";
  const [epSortCol, setEpSortCol] = useState<EpSortCol>("service");
  const [epSortAsc, setEpSortAsc] = useState(true);
  // Subscriptions
  const [subFilter, setSubFilter] = useState("");
  const [subGovFilter, setSubGovFilter] = useState<"all" | "gov" | "standard">("all");
  const [subStatusFilter, setSubStatusFilter] = useState<"all" | "active" | "inactive">("all");
  // Clusters
  const [clusterFilter, setClusterFilter] = useState("");
  const [clusterSubFilter, setClusterSubFilter] = useState("");
  const [clusterStatusFilter, setClusterStatusFilter] = useState<"all" | "active" | "inactive">("all");
  type ClusterSortCol = "name" | "subscription" | "status" | "monitors";
  const [clusterSortCol, setClusterSortCol] = useState<ClusterSortCol>("name");
  const [clusterSortAsc, setClusterSortAsc] = useState(true);

  async function loadAll() {
    const [rs, rr, rc, re, rsr, rsub, rcluster] = await Promise.all([
      adminFetch("/api/v1/admin/services/"),
      adminFetch("/api/v1/admin/regions/"),
      adminFetch("/api/v1/admin/components/"),
      adminFetch("/api/v1/admin/endpoints/"),
      adminFetch("/api/v1/admin/service-regions/"),
      adminFetch("/api/v1/admin/azure-subscriptions/"),
      adminFetch("/api/v1/admin/aks-clusters/"),
    ]);
    if (rs.ok) setServices(await rs.json());
    if (rr.ok) setRegions(await rr.json());
    if (rc.ok) setComponents(await rc.json());
    if (re.ok) setEndpoints(await re.json());
    if (rsr.ok) setServiceRegions(await rsr.json());
    if (rsub.ok) setSubscriptions(await rsub.json());
    if (rcluster.ok) setAksClusters(await rcluster.json());
  }

  useEffect(() => { loadAll(); }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────

  function getComponentsForService(serviceId: string): ComponentItem[] {
    return components.filter(c => c.service_id === serviceId && c.is_active);
  }

  function getRegionsForComponent(componentId: string): ServiceRegionOption[] {
    return serviceRegions.filter(sr => sr.component_id === componentId);
  }

  function getUnlinkedRegionsForComponent(componentId: string): RegionItem[] {
    const linked = getRegionsForComponent(componentId);
    const linkedNames = new Set(linked.map(sr => sr.region_name));
    return regions.filter(r => !linkedNames.has(r.name));
  }

  // ── Service CRUD ──────────────────────────────────────────────────────────

  function openServiceForm() {
    const nextOrder = services.length > 0 ? Math.max(...services.map(s => s.display_order)) + 10 : 0;
    setSvcName(""); setSvcSlug(""); setSvcDesc(""); setSvcOrder(nextOrder); setError(""); setShowServiceForm(true);
  }

  async function addService(e: React.FormEvent) {
    e.preventDefault(); setError("");
    const r = await adminFetch("/api/v1/admin/services/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: svcName, slug: svcSlug, description: svcDesc, display_order: svcOrder }),
    });
    if (!r.ok) { setError(`Failed to add service (${r.status})`); return; }
    setShowServiceForm(false);
    await loadAll();
  }

  async function saveServiceOrder(id: string, order: number) {
    await adminFetch(`/api/v1/admin/services/${id}/`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ display_order: order }),
    });
    await loadAll();
  }

  async function decommission(id: string) {
    if (!confirm("Decommission this service? It will be hidden from the public status board.")) return;
    await adminFetch(`/api/v1/admin/services/${id}/`, { method: "DELETE" });
    await loadAll();
  }

  async function removeService(id: string, name: string) {
    if (!confirm(`Permanently delete "${name}"? This will remove all its components, region links, and monitor endpoints. This cannot be undone.`)) return;
    const r = await adminFetch(`/api/v1/admin/services/${id}/?permanent=true`, { method: "DELETE" });
    if (!r.ok) { setError(`Failed to remove service (${r.status})`); return; }
    await loadAll();
  }

  async function reactivate(id: string) {
    const r = await adminFetch(`/api/v1/admin/services/${id}/`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_active: true }),
    });
    if (!r.ok) { setError(`Failed to reactivate service (${r.status})`); return; }
    await loadAll();
  }

  function openCloneService(svc: ServiceItem) {
    setCloneSvcSourceId(svc.id);
    setCloneSvcSourceName(svc.name);
    setCloneSvcWithRegions(false);
    setCloneSvcName(`${svc.name} (Copy)`);
    setCloneSvcSlug(`${svc.slug}-copy`);
    setCloneSvcError("");
  }

  async function submitCloneService(e: React.FormEvent) {
    e.preventDefault();
    if (!cloneSvcSourceId) return;
    setCloneSvcError(""); setCloneSvcSubmitting(true);
    try {
      const r = await adminFetch(`/api/v1/admin/services/${cloneSvcSourceId}/clone/`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: cloneSvcName.trim(), slug: cloneSvcSlug.trim(), clone_regions: cloneSvcWithRegions }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const first = (v: unknown): string | undefined => Array.isArray(v) ? v[0] : (typeof v === "string" ? v : undefined);
        setCloneSvcError(first(body?.name) ?? first(body?.slug) ?? first(body?.detail) ?? `HTTP ${r.status}`);
        return;
      }
      const data = await r.json();
      setCloneSvcSourceId(null);
      await loadAll();
      const regionNote = data.cloned_regions > 0 ? ` and ${data.cloned_regions} region link${data.cloned_regions !== 1 ? "s" : ""}` : "";
      setError(`Service "${data.name}" cloned with ${data.cloned_components} component${data.cloned_components !== 1 ? "s" : ""}${regionNote}.`);
    } finally { setCloneSvcSubmitting(false); }
  }

  // ── Component CRUD ────────────────────────────────────────────────────────

  function openComponentForm(serviceId: string) {
    setCompServiceId(serviceId); setCompName(""); setCompSlug(""); setCompOrder(0);
    setCompRegionIds([]); setError(""); setShowComponentForm(true);
  }

  async function addComponent(e: React.FormEvent) {
    e.preventDefault(); setError("");
    const r = await adminFetch("/api/v1/admin/components/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service: compServiceId,
        name: compName.trim(),
        slug: compSlug.trim(),
        display_order: isNaN(compOrder) ? 0 : compOrder,
      }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      const first = (v: unknown) => Array.isArray(v) ? v[0] : (typeof v === "string" ? v : undefined);
      const isSlugConflict = !!body?.slug;
      const detail = first(body?.slug) ?? first(body?.detail) ?? first(body?.name) ?? JSON.stringify(body);
      setError(`Failed to add component: ${detail}`);
      if (isSlugConflict) { setShowComponentForm(false); await loadAll(); }
      return;
    }
    const created = await r.json();
    // Link selected regions in parallel
    if (compRegionIds.length > 0) {
      const results = await Promise.all(
        compRegionIds.map(regionId =>
          adminFetch("/api/v1/admin/service-regions/", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ service: compServiceId, region: regionId, component: created.id }),
          })
        )
      );
      const failed = results.filter(r => !r.ok).length;
      if (failed > 0) setError(`Component created but ${failed} region(s) failed to link.`);
    }
    setShowComponentForm(false);
    await loadAll();
  }

  async function deactivateComponent(id: string, name: string) {
    if (!confirm(`Remove component "${name}"? Its service-region links will be preserved.`)) return;
    await adminFetch(`/api/v1/admin/components/${id}/`, { method: "DELETE" });
    await loadAll();
  }

  function openEditComp(comp: ComponentItem) {
    setEditCompId(comp.id);
    setEditCompName(comp.name);
    setEditCompSlug(comp.slug);
    setEditCompError("");
  }

  async function saveEditComp(e: React.FormEvent) {
    e.preventDefault();
    if (!editCompId) return;
    setEditCompError(""); setEditCompSubmitting(true);
    try {
      const r = await adminFetch(`/api/v1/admin/components/${editCompId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editCompName.trim(), slug: editCompSlug.trim() }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const first = (v: unknown): string | undefined => Array.isArray(v) ? v[0] : (typeof v === "string" ? v : undefined);
        setEditCompError(first(body?.name) ?? first(body?.slug) ?? first(body?.detail) ?? `HTTP ${r.status}`);
        return;
      }
      setEditCompId(null);
      await loadAll();
    } finally { setEditCompSubmitting(false); }
  }

  // ── Component-Region linking ──────────────────────────────────────────────

  function startLinkingComponent(componentId: string) {
    setLinkingComponentId(componentId);
    setLinkCompRegionIds([]);
  }

  function toggleLinkRegion(regionId: string) {
    setLinkCompRegionIds(prev =>
      prev.includes(regionId) ? prev.filter(id => id !== regionId) : [...prev, regionId]
    );
  }

  async function confirmLinkCompRegion() {
    if (!linkingComponentId || linkCompRegionIds.length === 0) return;
    setError("");
    const comp = components.find(c => c.id === linkingComponentId);
    if (!comp) return;
    const results = await Promise.all(
      linkCompRegionIds.map(regionId =>
        adminFetch("/api/v1/admin/service-regions/", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ service: comp.service_id, region: regionId, component: linkingComponentId }),
        })
      )
    );
    const failed = results.filter(r => !r.ok).length;
    if (failed > 0) setError(`${failed} region(s) failed to link.`);
    setLinkingComponentId(null); setLinkCompRegionIds([]);
    await loadAll();
  }

  async function unlinkRegion(srId: string, name: string) {
    if (!confirm(`Remove "${name}" deployment? This also removes any monitors tied to it.`)) return;
    const r = await adminFetch(`/api/v1/admin/service-regions/${srId}/`, { method: "DELETE" });
    if (!r.ok) { setError(`Failed to unlink region (${r.status})`); return; }
    await loadAll();
  }

  // ── Region CRUD ───────────────────────────────────────────────────────────

  async function addRegion(e: React.FormEvent) {
    e.preventDefault(); setError("");
    const r = await adminFetch("/api/v1/admin/regions/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: regName, slug: toSlug(regName) }),
    });
    if (!r.ok) { setError(`Failed to add region (${r.status})`); return; }
    setRegName(""); setShowRegionForm(false);
    await loadAll();
  }

  function openEditRegion(r: RegionItem) {
    setEditRegName(r.name); setEditRegSlug(r.slug); setEditRegOrder(r.display_order);
    setError(""); setEditRegion(r);
  }

  async function saveRegion(e: React.FormEvent) {
    e.preventDefault(); if (!editRegion) return; setError("");
    const r = await adminFetch(`/api/v1/admin/regions/${editRegion.id}/`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: editRegName.trim(), slug: editRegSlug.trim(), display_order: editRegOrder }),
    });
    if (!r.ok) { const b = await r.json().catch(() => ({})); setError(b?.name?.[0] ?? b?.slug?.[0] ?? b?.detail ?? `Failed to update region (${r.status})`); return; }
    setEditRegion(null); await loadAll();
  }

  async function deleteRegion(id: string, name: string) {
    if (!confirm(`Delete region "${name}"? This will also remove all component-region links and monitors tied to it.`)) return;
    const r = await adminFetch(`/api/v1/admin/regions/${id}/`, { method: "DELETE" });
    if (!r.ok) { setError(`Failed to delete region (${r.status})`); return; }
    await loadAll();
  }

  // ── Subscription CRUD ─────────────────────────────────────────────────────

  function openSubForm() {
    setEditSubId(null); setSubName(""); setSubSubscriptionId(""); setSubTenantId("");
    setSubClientId(""); setSubClientSecret(""); setSubSecretExpiresAt(""); setSubIsGov(false); setError(""); setShowSubForm(true);
  }

  function openEditSub(s: AzureSubscription) {
    setEditSubId(s.id); setSubName(s.name); setSubSubscriptionId(s.subscription_id);
    setSubTenantId(s.tenant_id); setSubClientId(s.client_id); setSubClientSecret("");
    setSubSecretExpiresAt(s.client_secret_expires_at ?? "");
    setSubIsGov(s.is_gov); setError(""); setShowSubForm(true);
  }

  function isValidUuid(v: string) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.trim());
  }

  async function saveSub(e: React.FormEvent) {
    e.preventDefault(); setError("");
    if (!isValidUuid(subSubscriptionId)) { setError("Subscription ID is not a valid UUID (expected: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)."); return; }
    if (!isValidUuid(subTenantId))       { setError("Tenant ID is not a valid UUID (expected: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)."); return; }
    if (!isValidUuid(subClientId))       { setError("Client ID is not a valid UUID (expected: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)."); return; }
    const payload: Record<string, unknown> = {
      name: subName.trim(), subscription_id: subSubscriptionId.trim(),
      tenant_id: subTenantId.trim(), client_id: subClientId.trim(), is_gov: subIsGov,
      client_secret_expires_at: subSecretExpiresAt.trim() || null,
    };
    if (subClientSecret.trim()) payload.client_secret = subClientSecret.trim();
    if (!editSubId && !subClientSecret.trim()) { setError("Client Secret is required when creating a subscription."); return; }
    if (!editSubId) payload.client_secret = subClientSecret.trim();
    const r = await adminFetch(
      editSubId ? `/api/v1/admin/azure-subscriptions/${editSubId}/` : "/api/v1/admin/azure-subscriptions/",
      { method: editSubId ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
    );
    if (!r.ok) { const b = await r.json().catch(() => ({})); setError(b?.name?.[0] ?? b?.detail ?? `Failed to save subscription (${r.status})`); return; }
    setShowSubForm(false); await loadAll();
  }

  async function deleteSub(id: string, name: string) {
    if (!confirm(`Delete subscription "${name}"? This will also remove all linked AKS clusters.`)) return;
    const r = await adminFetch(`/api/v1/admin/azure-subscriptions/${id}/`, { method: "DELETE" });
    if (!r.ok) { const b = await r.json().catch(() => ({})); setError(b?.detail ?? `Failed to delete subscription (${r.status})`); return; }
    await loadAll();
  }

  // ── AKS Cluster CRUD ──────────────────────────────────────────────────────

  function openClusterForm() {
    setEditClusterId(null); setClusterName(""); setClusterSubId(subscriptions[0]?.id ?? ""); setError(""); setShowClusterForm(true);
  }

  function openEditCluster(c: AksCluster) {
    setEditClusterId(c.id); setClusterName(c.name); setClusterSubId(c.subscription); setError(""); setShowClusterForm(true);
  }

  async function saveCluster(e: React.FormEvent) {
    e.preventDefault(); setError("");
    const r = await adminFetch(
      editClusterId ? `/api/v1/admin/aks-clusters/${editClusterId}/` : "/api/v1/admin/aks-clusters/",
      { method: editClusterId ? "PATCH" : "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: clusterName.trim(), subscription: clusterSubId }) }
    );
    if (!r.ok) { const b = await r.json().catch(() => ({})); setError(b?.name?.[0] ?? b?.detail ?? `Failed to save cluster (${r.status})`); return; }
    setShowClusterForm(false); await loadAll();
  }

  async function deleteCluster(id: string, name: string) {
    if (!confirm(`Delete cluster "${name}"? Monitors linked to this cluster will lose their AKS connection.`)) return;
    const r = await adminFetch(`/api/v1/admin/aks-clusters/${id}/`, { method: "DELETE" });
    if (!r.ok) { const b = await r.json().catch(() => ({})); setError(b?.detail ?? `Failed to delete cluster (${r.status})`); return; }
    await loadAll();
  }

  // ── Endpoint CRUD ─────────────────────────────────────────────────────────

  function openEndpointForm() {
    setEditEndpointId(null); setIsCloneMode(false);
    setEpServiceRegion(serviceRegions[0]?.id ?? "");
    setEpMonitorType("http");
    setEpMonitorMode("http");
    setEpUrl(""); setEpMethod("GET"); setEpExpectedStatus(200); setEpTimeout(10);
    setEpInterval(60); setEpThreshold(3);
    setEpAksClusterId(""); setEpAksSubFilter(""); setEpAksNamespace("default");
    setEpExpectedBody(""); setEpExpectedJsonPath(""); setEpExpectedJsonValue("");
    setProbeResult(null); setProbing(false);
    setAksProbeResult(null); setAksProbing(false);
    setError(""); setShowEndpointForm(true);
  }

  function _fillEndpointForm(ep: import("../../types/monitoring").MonitorEndpoint) {
    setEpServiceRegion(ep.service_region as unknown as string);
    setEpMonitorType(ep.monitor_type);
    setEpMonitorMode(ep.monitor_type as "http" | "aks_pod");
    setEpUrl(ep.url ?? ""); setEpMethod(ep.method ?? "GET");
    setEpExpectedStatus(ep.expected_status ?? 200);
    setEpExpectedBody((ep as unknown as Record<string, string>).expected_body ?? "");
    setEpExpectedJsonPath((ep as unknown as Record<string, string>).expected_json_path ?? "");
    setEpExpectedJsonValue((ep as unknown as Record<string, string>).expected_json_value ?? "");
    setEpTimeout(ep.timeout_seconds ?? 10);
    setEpInterval(ep.interval_seconds ?? 60); setEpThreshold(ep.failure_threshold ?? 3);
    setEpAksClusterId(ep.aks_cluster ?? "");
    const foundCluster = aksClusters.find(c => c.id === ep.aks_cluster);
    setEpAksSubFilter(foundCluster?.subscription ?? "");
    setEpAksNamespace(ep.aks_namespace ?? "default");
    setProbeResult(null); setProbing(false);
    setAksProbeResult(null); setAksProbing(false); setError("");
  }

  function openEditEndpointForm(ep: import("../../types/monitoring").MonitorEndpoint) {
    setEditEndpointId(ep.id); setIsCloneMode(false);
    _fillEndpointForm(ep);
    setShowEndpointForm(true);
  }

  function openCloneEndpointForm(ep: import("../../types/monitoring").MonitorEndpoint) {
    setEditEndpointId(null); setIsCloneMode(true);
    _fillEndpointForm(ep);
    setShowEndpointForm(true);
  }

  async function deleteEndpoint(id: string) {
    if (!confirm("Delete this monitoring endpoint? The monitor will stop checking this service.")) return;
    const r = await adminFetch(`/api/v1/admin/endpoints/${id}/`, { method: "DELETE" });
    if (!r.ok) { setError(`Failed to delete endpoint (${r.status})`); return; }
    await loadAll();
  }

  async function triggerEndpoint(id: string) {
    setTriggerState(prev => ({ ...prev, [id]: "running" }));
    try {
      const r = await adminFetch(`/api/v1/admin/endpoints/${id}/trigger/`, { method: "POST" });
      // Show "ok" if the trigger ran (even if the service is degraded/unhealthy).
      // Show "fail" only if the request itself failed (network / server error).
      const state = r.ok ? "ok" : "fail";
      setTriggerState(prev => ({ ...prev, [id]: state }));
      if (r.ok) {
        const data = await r.json();
        // Immediately apply effective_status from trigger response so the badge
        // updates before loadAll() completes (avoids stale-cache race).
        setEndpoints(prev => prev.map(ep =>
          ep.id === id ? { ...ep, effective_status: data.effective_status } : ep
        ));
        // Warn if a manual override is suppressing the monitoring result.
        if (data.override_active) {
          setError("Status override is active — monitoring result will not change the displayed status until the override is cleared.");
        }
        if (data.transition_error) {
          setError(`Status transition failed: ${data.transition_error}`);
        }
      }
      // Full refresh to sync last_checked_at, last_error, etc.
      await loadAll();
      // Reset icon back to idle after 3 s
      setTimeout(() => setTriggerState(prev => ({ ...prev, [id]: "idle" })), 3000);
    } catch {
      setTriggerState(prev => ({ ...prev, [id]: "fail" }));
      setTimeout(() => setTriggerState(prev => ({ ...prev, [id]: "idle" })), 3000);
    }
  }

  async function triggerProbe() {
    if (!epUrl.trim()) return;
    setProbing(true); setProbeResult(null);
    try {
      const r = await adminFetch("/api/v1/admin/endpoints/probe/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: epUrl.trim(), method: epMethod, expected_status: epExpectedStatus, timeout_seconds: epTimeout, expected_body: epExpectedBody.trim(), expected_json_path: epExpectedJsonPath.trim(), expected_json_value: epExpectedJsonValue.trim() }),
      });
      const data = await r.json();
      setProbeResult(data);
    } catch {
      setProbeResult({ ok: false, status_ok: false, body_ok: null, json_ok: null, json_actual: null, json_error: null, http_status: null, response_ms: null, body_snippet: null, error: "Network error — could not reach the server." });
    } finally {
      setProbing(false);
    }
  }

  async function triggerAksProbe() {
    if (!epAksClusterId) return;
    setAksProbing(true); setAksProbeResult(null);
    try {
      const r = await adminFetch("/api/v1/admin/endpoints/aks-probe/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          aks_cluster_id: epAksClusterId,
          aks_namespace: epAksNamespace.trim() || "default",
          timeout_seconds: epTimeout,
        }),
      });
      const text = await r.text();
      let data: AksProbeResult;
      try {
        data = JSON.parse(text);
      } catch {
        data = { ok: false, status: "outage", pod_total: null, pod_ready: null, detail: null, error: `Server error (${r.status}): ${text.slice(0, 200)}` };
      }
      setAksProbeResult(data);
    } catch (err) {
      setAksProbeResult({ ok: false, status: "outage", pod_total: null, pod_ready: null, detail: null, error: `Network error — could not reach the server. (${err instanceof Error ? err.message : String(err)})` });
    } finally {
      setAksProbing(false);
    }
  }

  async function addEndpoint(e: React.FormEvent) {
    e.preventDefault(); setError("");
    if (!epServiceRegion) { setError("Select a service region."); return; }
    const needsHttp = epMonitorMode === "http" || epMonitorMode === "both";
    const needsAks  = epMonitorMode === "aks_pod" || epMonitorMode === "both";
    if (needsHttp && !epUrl.trim()) { setError("Endpoint URL is required for Healthcheck Endpoint."); return; }
    if (needsAks && !epAksClusterId) { setError("Select an AKS cluster for AKS Pod Status."); return; }

    const shared = { service_region: epServiceRegion, interval_seconds: epInterval, failure_threshold: epThreshold };
    const httpPayload = { ...shared, monitor_type: "http", url: epUrl, method: epMethod, expected_status: epExpectedStatus, expected_body: epExpectedBody.trim(), expected_json_path: epExpectedJsonPath.trim(), expected_json_value: epExpectedJsonValue.trim(), timeout_seconds: epTimeout };
    const aksPayload  = { ...shared, monitor_type: "aks_pod", aks_cluster: epAksClusterId || null, aks_namespace: epAksNamespace.trim() || "default", timeout_seconds: epTimeout };

    // ── Edit mode ──
    if (editEndpointId) {
      // PATCH the existing endpoint using its original type's payload.
      // If "both" is selected we also POST a new endpoint for the other type.
      const patchPayload = epMonitorType === "aks_pod" ? aksPayload : httpPayload;
      const r = await adminFetch(`/api/v1/admin/endpoints/${editEndpointId}/`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchPayload),
      });
      if (!r.ok) {
        let detail = `Failed to update monitor.`;
        try {
          const body = await r.json();
          const firstVal = (v: unknown): string | undefined => {
            if (typeof v === "string") return v;
            if (Array.isArray(v) && v.length > 0) return String(v[0]);
            if (typeof v === "object" && v !== null) return firstVal(Object.values(v as Record<string, unknown>)[0]);
            return undefined;
          };
          const msg = firstVal(body?.detail) ?? firstVal(body?.url) ?? firstVal(body);
          if (msg) detail += ` ${msg}`;
        } catch { /* ignore */ }
        setError(detail); return;
      }
      // "Both" in edit mode: also POST a new endpoint for the other type.
      if (epMonitorMode === "both") {
        const newPayload = epMonitorType === "aks_pod" ? httpPayload : aksPayload;
        if (epMonitorType === "aks_pod" && needsHttp && epUrl.trim()) {
          const dup = endpoints.find(e => e.monitor_type === "http" && e.url === epUrl.trim() && (e.service_region as unknown as string) === epServiceRegion);
          if (dup) { setError(`A healthcheck endpoint with this URL already exists for this service region.`); return; }
        }
        const r2 = await adminFetch("/api/v1/admin/endpoints/", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newPayload),
        });
        if (!r2.ok) {
          let detail = "Updated existing monitor but failed to create the additional monitor.";
          try { const b = await r2.json(); detail += ` ${JSON.stringify(b)}`; } catch { /* ignore */ }
          setError(detail); await loadAll(); return;
        }
      }
      setEditEndpointId(null); setIsCloneMode(false); setShowEndpointForm(false); await loadAll(); return;
    }

    // ── Add / Clone mode: check for duplicate URLs before submitting ──
    if (needsHttp && epUrl.trim()) {
      const srId = epServiceRegion;
      const duplicate = endpoints.find(
        e => e.monitor_type === "http" && e.url === epUrl.trim() && (e.service_region as unknown as string) === srId
      );
      if (duplicate) {
        setError(`A healthcheck endpoint with this URL already exists for the selected service region: "${duplicate.service_region_label}". Change the URL or select a different service region.`);
        return;
      }
    }

    // ── Add mode: POST one or two endpoints ──
    const payloads = [
      ...(needsHttp ? [httpPayload] : []),
      ...(needsAks  ? [aksPayload]  : []),
    ];

    const results = await Promise.all(
      payloads.map(p => adminFetch("/api/v1/admin/endpoints/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(p),
      }))
    );
    const failed = results.filter(r => !r.ok);
    if (failed.length > 0) {
      let detail = `Failed to add ${failed.length} monitor(s).`;
      try {
        const body = await failed[0].json();
        const firstVal = (v: unknown): string | undefined => {
          if (typeof v === "string") return v;
          if (Array.isArray(v) && v.length > 0) return String(v[0]);
          if (typeof v === "object" && v !== null) return firstVal(Object.values(v as Record<string, unknown>)[0]);
          return undefined;
        };
        const msg = firstVal(body?.detail) ?? firstVal(body?.url) ?? firstVal(body?.non_field_errors) ?? firstVal(body);
        if (msg) detail += ` ${msg}`;
      } catch { /* ignore parse errors */ }
      setError(detail); return;
    }
    setShowEndpointForm(false); await loadAll();
  }

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredServices = services.filter(s => {
    const q = svcFilter.toLowerCase();
    const matchText = !q || s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q);
    const matchStatus = svcStatusFilter === "all" || (svcStatusFilter === "active" ? s.is_active : !s.is_active);
    return matchText && matchStatus;
  });

  const sortedServices = [...filteredServices].sort((a, b) => {
    let cmp = 0;
    switch (svcSortCol) {
      case "order":  cmp = a.display_order - b.display_order; break;
      case "name":   cmp = a.name.localeCompare(b.name); break;
      case "slug":   cmp = a.slug.localeCompare(b.slug); break;
      case "status": cmp = Number(b.is_active) - Number(a.is_active); break;
    }
    return svcSortAsc ? cmp : -cmp;
  });

  function toggleSvcSort(col: SvcSortCol) {
    if (svcSortCol === col) setSvcSortAsc(a => !a);
    else { setSvcSortCol(col); setSvcSortAsc(true); }
  }

  const filteredRegions = regions.filter(r => {
    const q = regFilter.toLowerCase();
    return !q || r.name.toLowerCase().includes(q) || r.slug.toLowerCase().includes(q);
  });

  const filteredEndpoints = endpoints.filter(ep => {
    const q = epSearchFilter.toLowerCase();
    const label = (ep.service_region_label || "").toLowerCase();
    const svcName = (ep.service_name || "").toLowerCase();
    const target = (ep.url || ep.aks_cluster_name || ep.aks_namespace || "").toLowerCase();
    const matchText = !q || label.includes(q) || svcName.includes(q) || target.includes(q);
    const matchType = epTypeFilter === "all" || ep.monitor_type === epTypeFilter;
    const epStatus = !ep.is_active ? "inactive" : ep.effective_status;
    const matchStatus = epStatusFilter === "all" || epStatus === epStatusFilter;
    const epRegion = (ep.service_region_label || "").split(" — ").at(-1) ?? "";
    const matchRegion = !epRegionFilter || epRegion === epRegionFilter;
    return matchText && matchType && matchStatus && matchRegion;
  });

  const statusOrder: Record<string, number> = { operational: 0, degraded: 1, outage: 2, maintenance: 3, inactive: 4 };
  const sortedEndpoints = [...filteredEndpoints].sort((a, b) => {
    let cmp = 0;
    switch (epSortCol) {
      case "service":          cmp = (a.service_name || "").localeCompare(b.service_name || ""); break;
      case "component_region": cmp = (a.service_region_label || "").localeCompare(b.service_region_label || ""); break;
      case "type":             cmp = a.monitor_type.localeCompare(b.monitor_type); break;
      case "target": {
        const ta = a.url || a.aks_cluster_name || a.aks_namespace || "";
        const tb = b.url || b.aks_cluster_name || b.aks_namespace || "";
        cmp = ta.localeCompare(tb);
        break;
      }
      case "interval":   cmp = a.interval_seconds - b.interval_seconds; break;
      case "status": {
        const sa = !a.is_active ? "inactive" : a.effective_status;
        const sb = !b.is_active ? "inactive" : b.effective_status;
        cmp = (statusOrder[sa] ?? 9) - (statusOrder[sb] ?? 9);
        break;
      }
    }
    return epSortAsc ? cmp : -cmp;
  });

  function toggleEpSort(col: EpSortCol) {
    if (epSortCol === col) setEpSortAsc(a => !a);
    else { setEpSortCol(col); setEpSortAsc(true); }
  }

  const filteredSubscriptions = subscriptions.filter(s => {
    const q = subFilter.toLowerCase();
    const matchText = !q || s.name.toLowerCase().includes(q) || s.subscription_id.toLowerCase().includes(q);
    const matchGov = subGovFilter === "all" || (subGovFilter === "gov" ? s.is_gov : !s.is_gov);
    const matchStatus = subStatusFilter === "all" || (subStatusFilter === "active" ? s.is_active : !s.is_active);
    return matchText && matchGov && matchStatus;
  });

  const filteredClusters = aksClusters.filter(c => {
    const q = clusterFilter.toLowerCase();
    const matchText = !q || c.name.toLowerCase().includes(q) || (c.subscription_name || "").toLowerCase().includes(q);
    const matchSub = !clusterSubFilter || c.subscription === clusterSubFilter;
    const matchStatus = clusterStatusFilter === "all" || (clusterStatusFilter === "active" ? c.is_active : !c.is_active);
    return matchText && matchSub && matchStatus;
  });

  const sortedClusters = [...filteredClusters].sort((a, b) => {
    let cmp = 0;
    switch (clusterSortCol) {
      case "name":         cmp = a.name.localeCompare(b.name); break;
      case "subscription": cmp = (a.subscription_name || "").localeCompare(b.subscription_name || ""); break;
      case "status":       cmp = Number(b.is_active) - Number(a.is_active); break;
      case "monitors":     cmp = (a.endpoint_count ?? 0) - (b.endpoint_count ?? 0); break;
    }
    return clusterSortAsc ? cmp : -cmp;
  });

  function toggleClusterSort(col: ClusterSortCol) {
    if (clusterSortCol === col) setClusterSortAsc(a => !a);
    else { setClusterSortCol(col); setClusterSortAsc(true); }
  }

  const counts = { services: filteredServices.length, regions: filteredRegions.length, endpoints: filteredEndpoints.length, subscriptions: filteredSubscriptions.length, clusters: filteredClusters.length };

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Service Registry</h1>
          <p className="ac-page-subtitle">Manage services, components, deployment regions, and health monitoring endpoints</p>
        </div>
        <div className="ac-page-header__actions">
          {tab === "services" && <button type="button" className="aptean-btn aptean-btn--primary" onClick={openServiceForm}>+ Add Service</button>}
          {tab === "regions" && <button type="button" className="aptean-btn aptean-btn--primary" onClick={() => { setRegName(""); setShowRegionForm(true); }}>+ Add Region</button>}
          {tab === "endpoints" && <button type="button" className="aptean-btn aptean-btn--primary" onClick={openEndpointForm}>+ Add Monitor</button>}
          {tab === "subscriptions" && <button type="button" className="aptean-btn aptean-btn--primary" onClick={openSubForm}>+ Add Subscription</button>}
          {tab === "clusters" && <button type="button" className="aptean-btn aptean-btn--primary" onClick={openClusterForm}>+ Add Cluster</button>}
        </div>
      </div>

      {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "1rem" }}>{error}</div>}

      <div className="ac-tabs">
        {(["services", "regions", "endpoints", "subscriptions", "clusters"] as Tab[]).map(t => (
          <button key={t} type="button" className={`ac-tab-btn${tab === t ? " ac-tab-btn--active" : ""}`} onClick={() => setTab(t)}>
            {{ services: "Services", regions: "Regions", endpoints: "Endpoints", subscriptions: "Subscriptions", clusters: "AKS Clusters" }[t]}
            <span style={{ marginLeft: "0.375rem", fontSize: "0.75rem", background: "var(--aptean-bg-overlay)", borderRadius: "9999px", padding: "0.05rem 0.4rem" }}>
              {counts[t]}
            </span>
          </button>
        ))}
      </div>

      {/* ── Services tab ── */}
      {tab === "services" && (
        <div className="ac-table-wrap">
          <div style={{ display: "flex", gap: "0.625rem", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <input
              type="search" placeholder="Search by name or slug…" value={svcFilter}
              onChange={e => setSvcFilter(e.target.value)}
              className="ac-input" style={{ flex: "1 1 200px", maxWidth: "300px" }}
            />
            <select value={svcStatusFilter} onChange={e => setSvcStatusFilter(e.target.value as typeof svcStatusFilter)} className="ac-select" style={{ minWidth: "130px" }}>
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
            {(svcFilter || svcStatusFilter !== "all") && (
              <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                onClick={() => { setSvcFilter(""); setSvcStatusFilter("all"); }}>Clear</button>
            )}
          </div>
          <table className="ac-table">
            <thead>
              {(() => {
                const th = (col: SvcSortCol): React.CSSProperties => ({ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", color: svcSortCol === col ? "var(--aptean-teal, #54B3BE)" : undefined });
                const arrow = (col: SvcSortCol) => svcSortCol === col ? (svcSortAsc ? " ▲" : " ▼") : " ⇅";
                return (
                  <tr>
                    <th style={{ width: "5%", ...th("order") }} onClick={() => toggleSvcSort("order")} title="Tab order on the public status dashboard">Order{arrow("order")}</th>
                    <th style={{ width: "30%", ...th("name") }} onClick={() => toggleSvcSort("name")}>Service{arrow("name")}</th>
                    <th style={th("slug")} onClick={() => toggleSvcSort("slug")}>Slug{arrow("slug")}</th>
                    <th style={th("status")} onClick={() => toggleSvcSort("status")}>Status{arrow("status")}</th>
                    <th className="ac-th--right">Actions</th>
                  </tr>
                );
              })()}
            </thead>
            <tbody>
              {filteredServices.length === 0
                ? <tr><td colSpan={5}><div className="ac-empty"><div className="ac-empty__title">{services.length === 0 ? "No services" : "No results"}</div><div className="ac-empty__body">{services.length === 0 ? "Add a service to start tracking its health." : "Try adjusting your filters."}</div></div></td></tr>
                : sortedServices.map(s => {
                  const isExpanded = expandedServiceId === s.id;
                  const svcs = getComponentsForService(s.id);
                  return (
                    <>
                      <tr key={s.id} style={{ background: isExpanded ? "var(--aptean-bg-overlay)" : undefined, verticalAlign: "middle" }}>
                        <td style={{ textAlign: "center" }}>
                          {editingOrderId === s.id ? (
                            <input
                              type="number"
                              min={0}
                              step={1}
                              autoFocus
                              value={editingOrderValue}
                              onChange={e => setEditingOrderValue(e.target.value)}
                              onBlur={() => {
                                const n = parseInt(editingOrderValue, 10);
                                if (!isNaN(n) && n !== s.display_order) saveServiceOrder(s.id, n);
                                setEditingOrderId(null);
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                if (e.key === "Escape") setEditingOrderId(null);
                              }}
                              style={{ width: "4rem", textAlign: "center", padding: "0.2rem 0.3rem", fontSize: "0.875rem", border: "1px solid var(--aptean-accent)", borderRadius: "4px", background: "var(--aptean-bg-base)", color: "var(--aptean-text-primary)" }}
                            />
                          ) : (
                            <button
                              type="button"
                              title="Click to change tab order"
                              onClick={() => { setEditingOrderId(s.id); setEditingOrderValue(String(s.display_order)); }}
                              style={{ background: "none", border: "none", cursor: "pointer", fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: "0.875rem", color: "var(--aptean-text-primary)", padding: "0.2rem 0.5rem", borderRadius: "4px", minWidth: "2.5rem" }}
                            >
                              {s.display_order}
                            </button>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0, width: "100%" }}
                            onClick={() => setExpandedServiceId(isExpanded ? null : s.id)}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                              <span style={{ fontSize: "0.7rem", color: "var(--aptean-text-secondary)", transform: isExpanded ? "rotate(90deg)" : "none", display: "inline-block", transition: "transform 150ms" }}>▶</span>
                              <span className="ac-row-title">{s.name}</span>
                            </div>
                            {s.description && <div className="ac-row-sub">{s.description}</div>}
                            <div style={{ marginTop: "0.15rem" }}>
                              <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
                                {svcs.length} component{svcs.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </button>
                        </td>
                        <td className="ac-td--mono">{s.slug}</td>
                        <td><span className={`ac-badge ${s.is_active ? "ac-badge--operational" : "ac-badge--neutral"}`}>{s.is_active ? "Active" : "Inactive"}</span></td>
                        <td className="ac-td--right ac-td--actions">
                          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                            {s.is_active ? (
                              <>
                                <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => openCloneService(s)}>Clone</button>
                                <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => decommission(s.id)}>Decommission</button>
                              </>
                            ) : (
                              <>
                                <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => openCloneService(s)}>Clone</button>
                                <button type="button" className="aptean-btn aptean-btn--secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => reactivate(s.id)}>Reactivate</button>
                                <button type="button" className="aptean-btn aptean-btn--danger" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => removeService(s.id, s.name)}>Delete</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* ── Expanded: component list ── */}
                      {isExpanded && (
                        <tr key={`${s.id}-components`} className="ac-detail-row">
                          <td colSpan={5}>
                            <div className="ac-detail-panel" style={{ paddingTop: "0.75rem", paddingBottom: "0.75rem" }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.625rem" }}>
                                <span style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--aptean-text-secondary)" }}>
                                  Components
                                </span>
                                <button
                                  type="button"
                                  className="aptean-btn aptean-btn--secondary"
                                  style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}
                                  onClick={() => openComponentForm(s.id)}
                                >
                                  + Add Component
                                </button>
                              </div>

                              {svcs.length === 0 ? (
                                <p style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)" }}>
                                  No components yet. Components represent individual deployable units (e.g. "Database", "API", "Web App") within this service.
                                </p>
                              ) : (
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                                  <thead>
                                    <tr style={{ borderBottom: "1px solid var(--aptean-border-default)" }}>
                                      <th style={{ textAlign: "left", padding: "0.375rem 0.5rem", fontWeight: 600, color: "var(--aptean-text-secondary)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Component</th>
                                      <th style={{ textAlign: "left", padding: "0.375rem 0.5rem", fontWeight: 600, color: "var(--aptean-text-secondary)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Deployed Regions</th>
                                      <th style={{ textAlign: "right", padding: "0.375rem 0.5rem" }} />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {svcs.map(comp => {
                                      const compRegions = getRegionsForComponent(comp.id);
                                      const unlinkedR = getUnlinkedRegionsForComponent(comp.id);
                                      const isLinkingThis = linkingComponentId === comp.id;
                                      return (
                                        <tr key={comp.id} style={{ borderBottom: "1px solid var(--aptean-border-default)" }}>
                                          <td style={{ padding: "0.5rem" }}>
                                            <div style={{ fontWeight: 500 }}>{comp.name}</div>
                                            <div style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>{comp.slug}</div>
                                          </td>
                                          <td style={{ padding: "0.5rem" }}>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", alignItems: "center" }}>
                                              {compRegions.length === 0
                                                ? <span style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)" }}>No regions</span>
                                                : compRegions.map(sr => (
                                                  <span key={sr.id} style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", background: "var(--aptean-bg-overlay)", border: "1px solid var(--aptean-border-default)", borderRadius: "4px", padding: "0.1rem 0.35rem 0.1rem 0.5rem", fontSize: "0.75rem" }}>
                                                    {sr.region_name}
                                                    <button type="button"
                                                      onClick={() => unlinkRegion(sr.id, `${comp.name} / ${sr.region_name}`)}
                                                      title={`Remove ${comp.name} from ${sr.region_name}`}
                                                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--aptean-text-secondary)", fontSize: "0.7rem", lineHeight: 1, padding: "0 0.1rem" }}>
                                                      &#10005;
                                                    </button>
                                                  </span>
                                                ))
                                              }
                                              {/* Inline add region */}
                                              {!isLinkingThis && unlinkedR.length > 0 && (
                                                <button type="button"
                                                  onClick={() => startLinkingComponent(comp.id)}
                                                  style={{ fontSize: "0.75rem", background: "none", border: "1px dashed var(--aptean-border-default)", borderRadius: "4px", padding: "0.1rem 0.4rem", cursor: "pointer", color: "var(--aptean-text-secondary)", lineHeight: 1.4 }}>
                                                  + Add Region
                                                </button>
                                              )}
                                              {isLinkingThis && (
                                                <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginTop: "0.25rem" }}>
                                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
                                                    {unlinkedR.map(r => (
                                                      <label key={r.id} style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.8125rem", cursor: "pointer", background: linkCompRegionIds.includes(r.id) ? "var(--aptean-teal-muted, rgba(84,179,190,0.15))" : "var(--aptean-bg-overlay)", border: `1px solid ${linkCompRegionIds.includes(r.id) ? "var(--aptean-accent)" : "var(--aptean-border-default)"}`, borderRadius: "4px", padding: "0.15rem 0.5rem" }}>
                                                        <input type="checkbox" checked={linkCompRegionIds.includes(r.id)} onChange={() => toggleLinkRegion(r.id)} style={{ margin: 0 }} />
                                                        {r.name}
                                                      </label>
                                                    ))}
                                                  </div>
                                                  <div style={{ display: "flex", gap: "0.25rem" }}>
                                                    <button type="button" className="aptean-btn aptean-btn--primary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }} onClick={confirmLinkCompRegion} disabled={linkCompRegionIds.length === 0}>
                                                      Link {linkCompRegionIds.length > 0 ? `(${linkCompRegionIds.length})` : ""}
                                                    </button>
                                                    <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }} onClick={() => setLinkingComponentId(null)}>Cancel</button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                          <td style={{ padding: "0.5rem", textAlign: "right" }}>
                                            <div style={{ display: "flex", gap: "0.3rem", justifyContent: "flex-end" }}>
                                              <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.7rem", padding: "0.2rem 0.5rem" }}
                                                onClick={() => openEditComp(comp)}>
                                                Edit
                                              </button>
                                              <button type="button" className="aptean-btn aptean-btn--danger" style={{ fontSize: "0.7rem", padding: "0.2rem 0.5rem" }}
                                                onClick={() => deactivateComponent(comp.id, comp.name)}>
                                                Delete
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Regions tab ── */}
      {tab === "regions" && (
        <div className="ac-table-wrap">
          <div style={{ display: "flex", gap: "0.625rem", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <input
              type="search" placeholder="Search by name or slug…" value={regFilter}
              onChange={e => setRegFilter(e.target.value)}
              className="ac-input" style={{ flex: "1 1 200px", maxWidth: "300px" }}
            />
            {regFilter && (
              <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                onClick={() => setRegFilter("")}>Clear</button>
            )}
          </div>
          <table className="ac-table">
            <thead><tr><th>Region</th><th>Slug</th><th>Display Order</th><th className="ac-th--right">Actions</th></tr></thead>
            <tbody>
              {filteredRegions.length === 0
                ? <tr><td colSpan={4}><div className="ac-empty"><div className="ac-empty__title">{regions.length === 0 ? "No regions" : "No results"}</div><div className="ac-empty__body">{regions.length === 0 ? "Regions represent deployment locations (e.g. ANZ, EU, US-East)." : "Try adjusting your search."}</div></div></td></tr>
                : filteredRegions.map(r => (
                  <tr key={r.id}>
                    <td className="ac-row-title">{r.name}</td>
                    <td className="ac-td--mono">{r.slug}</td>
                    <td className="ac-td--muted">{r.display_order}</td>
                    <td className="ac-td--right ac-td--actions">
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => openEditRegion(r)}>Edit</button>
                        <button type="button" className="aptean-btn aptean-btn--danger" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => deleteRegion(r.id, r.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Endpoints tab ── */}
      {tab === "endpoints" && (
        <div className="ac-table-wrap">
          {serviceRegions.length === 0 && (
            <div className="ac-alert ac-alert--info" style={{ margin: "0 0 0.75rem" }}>
              No component-region pairs exist yet. Go to the <strong>Services</strong> tab, expand a service, add components, and link them to regions. Then come back to add monitors.
            </div>
          )}
          <div style={{ display: "flex", gap: "0.625rem", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <input
              type="search" placeholder="Search by service, component or region…" value={epSearchFilter}
              onChange={e => setEpSearchFilter(e.target.value)}
              className="ac-input" style={{ flex: "1 1 200px", maxWidth: "280px" }}
            />
            <select value={epTypeFilter} onChange={e => setEpTypeFilter(e.target.value as typeof epTypeFilter)} className="ac-select" style={{ minWidth: "130px" }}>
              <option value="all">All types</option>
              <option value="http">HTTP only</option>
              <option value="aks_pod">AKS Pod only</option>
            </select>
            <select value={epStatusFilter} onChange={e => setEpStatusFilter(e.target.value as typeof epStatusFilter)} className="ac-select" style={{ minWidth: "150px" }}>
              <option value="all">All statuses</option>
              <option value="operational">Operational</option>
              <option value="degraded">Degraded</option>
              <option value="outage">Outage</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={epRegionFilter} onChange={e => setEpRegionFilter(e.target.value)} className="ac-select" style={{ minWidth: "150px" }}>
              <option value="">All regions</option>
              {[...regions].sort((a, b) => a.name.localeCompare(b.name)).map(r => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
            </select>
            {(epSearchFilter || epTypeFilter !== "all" || epStatusFilter !== "all" || epRegionFilter) && (
              <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                onClick={() => { setEpSearchFilter(""); setEpTypeFilter("all"); setEpStatusFilter("all"); setEpRegionFilter(""); }}>Clear</button>
            )}
          </div>
          <table className="ac-table">
            <thead>
              {(() => {
                const thStyle = (col: EpSortCol): React.CSSProperties => ({
                  cursor: "pointer", userSelect: "none",
                  color: epSortCol === col ? "var(--aptean-teal, #54B3BE)" : undefined,
                  whiteSpace: "nowrap",
                });
                const arrow = (col: EpSortCol) => epSortCol === col ? (epSortAsc ? " ▲" : " ▼") : " ⇅";
                return (
                  <tr>
                    <th style={{ width: "16%", ...thStyle("service") }} onClick={() => toggleEpSort("service")}>Service{arrow("service")}</th>
                    <th style={{ width: "20%", ...thStyle("component_region") }} onClick={() => toggleEpSort("component_region")}>Component / Region{arrow("component_region")}</th>
                    <th style={thStyle("type")} onClick={() => toggleEpSort("type")}>Type{arrow("type")}</th>
                    <th style={thStyle("target")} onClick={() => toggleEpSort("target")}>Target{arrow("target")}</th>
                    <th style={thStyle("interval")} onClick={() => toggleEpSort("interval")}>Interval{arrow("interval")}</th>
                    <th style={thStyle("status")} onClick={() => toggleEpSort("status")}>Status{arrow("status")}</th>
                    <th className="ac-th--right">Actions</th>
                  </tr>
                );
              })()}
            </thead>
            <tbody>
              {filteredEndpoints.length === 0
                ? <tr><td colSpan={7}><div className="ac-empty">
                    <div className="ac-empty__title">{endpoints.length === 0 ? "No monitoring endpoints" : "No results"}</div>
                    <div className="ac-empty__body">{endpoints.length === 0 ? "Add a monitor to automatically check component health and update the status board." : "Try adjusting your filters."}</div>
                  </div></td></tr>
                : sortedEndpoints.map(ep => (
                  <tr key={ep.id}>
                    <td>
                      <div className="ac-row-title">{ep.service_name ?? "—"}</div>
                    </td>
                    <td>
                      <div className="ac-row-title">{ep.service_region_label?.split(" — ")[0] ?? "—"}</div>
                      <div className="ac-row-sub">{ep.service_region_label?.split(" — ")[1] ?? ""}</div>
                    </td>
                    <td><MonitorTypeBadge type={ep.monitor_type} /></td>
                    <td className="ac-td--mono" style={{ fontSize: "0.8125rem", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {ep.monitor_type === "aks_pod"
                        ? (() => {
                            const clusterName = ep.aks_cluster_display
                              ? ep.aks_cluster_display.split(" / ").at(-1)
                              : ep.aks_cluster_name || "—";
                            const ns = ep.aks_namespace || "default";
                            return `${clusterName}.${ns}`;
                          })()
                        : ep.url}
                    </td>
                    <td className="ac-td--muted">{ep.interval_seconds}s</td>
                    <td>
                      {!ep.is_active ? (
                        <span className="ac-badge ac-badge--neutral">Inactive</span>
                      ) : ep.effective_status === "operational" ? (
                        <span className="ac-badge ac-badge--operational">Operational</span>
                      ) : ep.effective_status === "degraded" ? (
                        <span className="ac-badge ac-badge--degraded">Degraded</span>
                      ) : ep.effective_status === "outage" ? (
                        <span className="ac-badge ac-badge--outage" title={ep.last_error ?? undefined}>Outage</span>
                      ) : ep.effective_status === "maintenance" ? (
                        <span className="ac-badge ac-badge--maintenance">Maintenance</span>
                      ) : (
                        <span className="ac-badge ac-badge--neutral">Unknown</span>
                      )}
                    </td>
                    <td className="ac-td--right ac-td--actions">
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", alignItems: "center" }}>
                        {/* Trigger now button */}
                        {(() => {
                          const ts = triggerState[ep.id] ?? "idle";
                          const color = ts === "ok" ? "#107c10" : ts === "fail" ? "#a4262c" : "var(--aptean-text-secondary)";
                          const spin = ts === "running";
                          return (
                            <button
                              type="button"
                              title="Trigger health check now"
                              disabled={spin}
                              onClick={() => triggerEndpoint(ep.id)}
                              style={{ background: "none", border: "1px solid var(--aptean-border-default)", borderRadius: "6px", padding: "0.3rem 0.45rem", cursor: spin ? "default" : "pointer", color, lineHeight: 0, display: "flex", alignItems: "center" }}
                            >
                              <svg
                                width="14" height="14" viewBox="0 0 16 16" fill="none"
                                style={{ animation: spin ? "spin 0.8s linear infinite" : "none" }}
                              >
                                {ts === "ok" ? (
                                  <>
                                    <circle cx="8" cy="8" r="7" stroke="#107c10" strokeWidth="1.5"/>
                                    <path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="#107c10" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                  </>
                                ) : ts === "fail" ? (
                                  <>
                                    <circle cx="8" cy="8" r="7" stroke="#a4262c" strokeWidth="1.5"/>
                                    <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#a4262c" strokeWidth="1.75" strokeLinecap="round"/>
                                  </>
                                ) : (
                                  <path d="M13.5 8A5.5 5.5 0 1 1 8 2.5a5.5 5.5 0 0 1 3.89 1.61M13.5 2.5v3.5H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                )}
                              </svg>
                            </button>
                          );
                        })()}
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => openCloneEndpointForm(ep)} title="Clone this endpoint">Clone</button>
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => openEditEndpointForm(ep)}>Edit</button>
                        <button type="button" className="aptean-btn aptean-btn--danger" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => deleteEndpoint(ep.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Subscriptions tab ── */}
      {tab === "subscriptions" && (
        <div className="ac-table-wrap">
          <div style={{ display: "flex", gap: "0.625rem", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <input
              type="search" placeholder="Search by name or subscription ID…" value={subFilter}
              onChange={e => setSubFilter(e.target.value)}
              className="ac-input" style={{ flex: "1 1 200px", maxWidth: "300px" }}
            />
            <select value={subGovFilter} onChange={e => setSubGovFilter(e.target.value as typeof subGovFilter)} className="ac-select" style={{ minWidth: "140px" }}>
              <option value="all">All clouds</option>
              <option value="gov">Gov cloud only</option>
              <option value="standard">Standard only</option>
            </select>
            <select value={subStatusFilter} onChange={e => setSubStatusFilter(e.target.value as typeof subStatusFilter)} className="ac-select" style={{ minWidth: "130px" }}>
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
            {(subFilter || subGovFilter !== "all" || subStatusFilter !== "all") && (
              <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                onClick={() => { setSubFilter(""); setSubGovFilter("all"); setSubStatusFilter("all"); }}>Clear</button>
            )}
          </div>
          <table className="ac-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Subscription ID</th>
                <th>Tenant ID</th>
                <th>Client ID</th>
                <th>Secret Expires</th>
                <th>Gov</th>
                <th>Clusters</th>
                <th>Status</th>
                <th className="ac-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.length === 0
                ? <tr><td colSpan={9}><div className="ac-empty"><div className="ac-empty__title">{subscriptions.length === 0 ? "No subscriptions" : "No results"}</div><div className="ac-empty__body">{subscriptions.length === 0 ? "Add an Azure subscription to enable AKS Pod monitoring." : "Try adjusting your filters."}</div></div></td></tr>
                : filteredSubscriptions.map(s => {
                  const expiry = s.client_secret_expires_at ? new Date(s.client_secret_expires_at) : null;
                  const today = new Date(); today.setHours(0,0,0,0);
                  const daysLeft = expiry ? Math.ceil((expiry.getTime() - today.getTime()) / 86400000) : null;
                  const expiryBadge = expiry == null
                    ? <span className="ac-td--muted" style={{ fontSize: "0.8rem" }}>—</span>
                    : daysLeft != null && daysLeft < 0
                      ? <span className="ac-badge ac-badge--outage" title={s.client_secret_expires_at!}>Expired</span>
                      : daysLeft != null && daysLeft <= 30
                        ? <span className="ac-badge ac-badge--degraded" title={s.client_secret_expires_at!}>Expires in {daysLeft}d</span>
                        : <span style={{ fontSize: "0.8rem" }}>{s.client_secret_expires_at}</span>;
                  return (
                  <tr key={s.id}>
                    <td className="ac-row-title">{s.name}{s.is_gov && <span className="ac-badge ac-badge--info" style={{ marginLeft: "0.5rem", fontSize: "0.7rem" }}>Gov</span>}</td>
                    <td className="ac-td--mono" style={{ fontSize: "0.8rem" }}>{s.subscription_id}</td>
                    <td className="ac-td--mono" style={{ fontSize: "0.8rem" }}>{s.tenant_id}</td>
                    <td className="ac-td--mono" style={{ fontSize: "0.8rem" }}>{s.client_id}</td>
                    <td>{expiryBadge}</td>
                    <td>{s.is_gov ? "Yes" : "No"}</td>
                    <td className="ac-td--muted">{s.cluster_count}</td>
                    <td>{s.is_active ? <span className="ac-badge ac-badge--operational">Active</span> : <span className="ac-badge ac-badge--neutral">Inactive</span>}</td>
                    <td className="ac-td--right ac-td--actions">
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => openEditSub(s)}>Edit</button>
                        <button type="button" className="aptean-btn aptean-btn--danger" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => deleteSub(s.id, s.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── AKS Clusters tab ── */}
      {tab === "clusters" && (
        <div className="ac-table-wrap">
          <div style={{ display: "flex", gap: "0.625rem", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <input
              type="search" placeholder="Search by cluster or subscription name…" value={clusterFilter}
              onChange={e => setClusterFilter(e.target.value)}
              className="ac-input" style={{ flex: "1 1 200px", maxWidth: "300px" }}
            />
            <select value={clusterSubFilter} onChange={e => setClusterSubFilter(e.target.value)} className="ac-select" style={{ minWidth: "180px" }}>
              <option value="">All subscriptions</option>
              {subscriptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={clusterStatusFilter} onChange={e => setClusterStatusFilter(e.target.value as typeof clusterStatusFilter)} className="ac-select" style={{ minWidth: "130px" }}>
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
            {(clusterFilter || clusterSubFilter || clusterStatusFilter !== "all") && (
              <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                onClick={() => { setClusterFilter(""); setClusterSubFilter(""); setClusterStatusFilter("all"); }}>Clear</button>
            )}
          </div>
          <table className="ac-table">
            <thead>
              {(() => {
                const th = (col: ClusterSortCol): React.CSSProperties => ({ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", color: clusterSortCol === col ? "var(--aptean-teal, #54B3BE)" : undefined });
                const arrow = (col: ClusterSortCol) => clusterSortCol === col ? (clusterSortAsc ? " ▲" : " ▼") : " ⇅";
                return (
                  <tr>
                    <th style={th("name")} onClick={() => toggleClusterSort("name")}>Cluster Name{arrow("name")}</th>
                    <th style={th("subscription")} onClick={() => toggleClusterSort("subscription")}>Subscription{arrow("subscription")}</th>
                    <th style={th("status")} onClick={() => toggleClusterSort("status")}>Status{arrow("status")}</th>
                    <th style={th("monitors")} onClick={() => toggleClusterSort("monitors")}>Monitors{arrow("monitors")}</th>
                    <th className="ac-th--right">Actions</th>
                  </tr>
                );
              })()}
            </thead>
            <tbody>
              {filteredClusters.length === 0
                ? <tr><td colSpan={5}><div className="ac-empty"><div className="ac-empty__title">{aksClusters.length === 0 ? "No AKS clusters" : "No results"}</div><div className="ac-empty__body">{aksClusters.length === 0 ? "Add the AKS clusters you want to monitor after adding a subscription." : "Try adjusting your filters."}</div></div></td></tr>
                : sortedClusters.map(c => (
                  <tr key={c.id}>
                    <td className="ac-row-title">{c.name}</td>
                    <td className="ac-td--muted">{c.subscription_name}</td>
                    <td>{c.is_active ? <span className="ac-badge ac-badge--operational">Active</span> : <span className="ac-badge ac-badge--neutral">Inactive</span>}</td>
                    <td className="ac-td--muted">{c.endpoint_count}</td>
                    <td className="ac-td--right ac-td--actions">
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => openEditCluster(c)}>Edit</button>
                        <button type="button" className="aptean-btn aptean-btn--danger" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => deleteCluster(c.id, c.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add Service dialog ── */}
      {showServiceForm && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowServiceForm(false)}>
          <div className="ac-dialog" style={{ maxWidth: "480px" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Add Service</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowServiceForm(false)}>&#10005;</button>
            </div>
            <form onSubmit={addService}>
              <div className="ac-dialog__body">
                {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
                <div className="ac-field">
                  <label>Name *</label>
                  <input type="text" className="aptean-input" value={svcName}
                    onChange={e => { setSvcName(e.target.value); setSvcSlug(toSlug(e.target.value)); }}
                    placeholder="e.g. Aptean F&B - JustFood Edition" required />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>This is the section header on the public status board.</span>
                </div>
                <div className="ac-field">
                  <label>Slug *</label>
                  <input type="text" className="aptean-input" value={svcSlug} onChange={e => setSvcSlug(e.target.value)} placeholder="aptean-fb-justfood" required />
                </div>
                <div className="ac-field">
                  <label>Description</label>
                  <textarea className="aptean-input" rows={2} value={svcDesc} onChange={e => setSvcDesc(e.target.value)} />
                </div>
                <div className="ac-field">
                  <label>Tab order</label>
                  <input type="number" className="aptean-input" min={0} step={1}
                    value={svcOrder} onChange={e => setSvcOrder(Number(e.target.value))} style={{ width: "8rem" }} />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
                    Controls the left-to-right tab order on the public status dashboard. Lower numbers appear first.
                  </span>
                </div>
                <p style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", marginTop: "0.5rem" }}>
                  After adding the service, expand it to add <strong>components</strong> (e.g. "Database", "API", "Web App") and link each to regions.
                </p>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowServiceForm(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary">Add Service</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Clone Service dialog ── */}
      {cloneSvcSourceId && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setCloneSvcSourceId(null)}>
          <div className="ac-dialog" style={{ maxWidth: "460px" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Clone Service</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setCloneSvcSourceId(null)}>&#10005;</button>
            </div>
            <form onSubmit={submitCloneService}>
              <div className="ac-dialog__body">
                <p style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", marginBottom: "1rem" }}>
                  Cloning <strong>{cloneSvcSourceName}</strong>.
                </p>
                <div className="ac-field" style={{ marginBottom: "1rem" }}>
                  <label style={{ marginBottom: "0.5rem", display: "block" }}>Clone Options *</label>
                  {([
                    [false, "Clone components only", "Copy all components without their region links."],
                    [true,  "Clone components with regions", "Copy all components and their region links."],
                  ] as [boolean, string, string][]).map(([val, title, desc]) => (
                    <label key={String(val)} className="ac-checkbox-item" style={{ display: "flex", alignItems: "flex-start", gap: "0.625rem", padding: "0.625rem 0.875rem", marginBottom: "0.5rem", border: `1px solid ${cloneSvcWithRegions === val ? "var(--aptean-teal, #54B3BE)" : "var(--aptean-border-default)"}`, borderRadius: "6px", cursor: "pointer" }}>
                      <input type="radio" name="cloneMode" style={{ marginTop: "0.125rem", flexShrink: 0 }} checked={cloneSvcWithRegions === val} onChange={() => setCloneSvcWithRegions(val)} />
                      <span>
                        <span style={{ display: "block", fontWeight: 500 }}>{title}</span>
                        <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>{desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {cloneSvcError && <div className="ac-alert ac-alert--error">{cloneSvcError}</div>}
                <div className="ac-field">
                  <label htmlFor="clone-svc-name">New Service Name *</label>
                  <input
                    id="clone-svc-name"
                    type="text"
                    className="aptean-input"
                    value={cloneSvcName}
                    onChange={e => { setCloneSvcName(e.target.value); setCloneSvcSlug(toSlug(e.target.value)); }}
                    required
                    autoFocus
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="clone-svc-slug">Slug *</label>
                  <input
                    id="clone-svc-slug"
                    type="text"
                    className="aptean-input"
                    value={cloneSvcSlug}
                    onChange={e => setCloneSvcSlug(e.target.value)}
                    required
                  />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
                    Must be unique. Used in URLs and API references.
                  </span>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setCloneSvcSourceId(null)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={cloneSvcSubmitting}>
                  {cloneSvcSubmitting ? "Cloning…" : "Clone Service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Component dialog ── */}
      {showComponentForm && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowComponentForm(false)}>
          <div className="ac-dialog" style={{ maxWidth: "440px" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Add Component</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowComponentForm(false)}>&#10005;</button>
            </div>
            <form onSubmit={addComponent}>
              <div className="ac-dialog__body">
                {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
                <p style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", marginBottom: "0.75rem" }}>
                  Under: <strong>{services.find(s => s.id === compServiceId)?.name}</strong>
                </p>
                <div className="ac-field">
                  <label>Component Name *</label>
                  <input type="text" className="aptean-input" value={compName}
                    onChange={e => { setCompName(e.target.value); setCompSlug(toSlug(e.target.value)); }}
                    placeholder="e.g. JustFood - Database" required />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>This appears as a row on the public status board.</span>
                </div>
                <div className="ac-field">
                  <label>Slug *</label>
                  <input type="text" className="aptean-input" value={compSlug} onChange={e => setCompSlug(e.target.value)} required />
                </div>
                <div className="ac-field">
                  <label>Display Order</label>
                  <input type="number" className="aptean-input" value={compOrder} onChange={e => setCompOrder(Number(e.target.value))} min={0} />
                </div>
                {regions.length > 0 && (
                  <div className="ac-field">
                    <label>
                      Deploy to Regions
                      {compRegionIds.length > 0 && (
                        <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "var(--aptean-teal)", fontWeight: 600 }}>
                          {compRegionIds.length} selected
                        </span>
                      )}
                    </label>
                    <div className="ac-checkbox-list">
                      {regions.map(r => (
                        <label key={r.id} className="ac-checkbox-item">
                          <input
                            type="checkbox"
                            checked={compRegionIds.includes(r.id)}
                            onChange={() => setCompRegionIds(prev =>
                              prev.includes(r.id) ? prev.filter(id => id !== r.id) : [...prev, r.id]
                            )}
                          />
                          {r.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowComponentForm(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary">Add Component</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Component dialog ── */}
      {editCompId && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setEditCompId(null)}>
          <div className="ac-dialog" style={{ maxWidth: "420px" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Edit Component</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setEditCompId(null)}>&#10005;</button>
            </div>
            <form onSubmit={saveEditComp}>
              <div className="ac-dialog__body">
                {editCompError && <div className="ac-alert ac-alert--error" style={{ marginBottom: "0.75rem" }}>{editCompError}</div>}
                <div className="ac-field">
                  <label htmlFor="edit-comp-name">Component Name *</label>
                  <input
                    id="edit-comp-name" type="text" className="aptean-input"
                    value={editCompName} onChange={e => setEditCompName(e.target.value)}
                    required autoFocus
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="edit-comp-slug">Slug *</label>
                  <input
                    id="edit-comp-slug" type="text" className="aptean-input"
                    value={editCompSlug} onChange={e => setEditCompSlug(e.target.value)}
                    required
                  />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
                    Changing the slug may break existing links. Rename with care.
                  </span>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setEditCompId(null)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={editCompSubmitting}>
                  {editCompSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Region dialog ── */}
      {showRegionForm && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowRegionForm(false)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Add Region</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowRegionForm(false)}>&#10005;</button>
            </div>
            <form onSubmit={addRegion}>
              <div className="ac-dialog__body">
                <div className="ac-field">
                  <label>Region Name *</label>
                  <input type="text" className="aptean-input" value={regName} onChange={e => setRegName(e.target.value)} placeholder="e.g. ANZ, EU, US-East" required />
                </div>
                <p style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", marginTop: "0.5rem" }}>
                  After adding a region, expand a service → expand a component → use <em>+ Add Region</em> to link it.
                </p>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowRegionForm(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary">Add Region</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Region dialog ── */}
      {editRegion && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setEditRegion(null)}>
          <div className="ac-dialog" style={{ maxWidth: "420px" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Edit Region</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setEditRegion(null)}>&#10005;</button>
            </div>
            <form onSubmit={saveRegion}>
              <div className="ac-dialog__body">
                {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
                <div className="ac-field">
                  <label>Region Name *</label>
                  <input type="text" className="aptean-input" value={editRegName}
                    onChange={e => { setEditRegName(e.target.value); setEditRegSlug(toSlug(e.target.value)); }}
                    required />
                </div>
                <div className="ac-field">
                  <label>Slug *</label>
                  <input type="text" className="aptean-input" value={editRegSlug}
                    onChange={e => setEditRegSlug(e.target.value)} required />
                </div>
                <div className="ac-field">
                  <label>Display Order</label>
                  <input type="number" className="aptean-input" value={editRegOrder}
                    onChange={e => setEditRegOrder(Number(e.target.value))} min={0} />
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setEditRegion(null)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add / Edit Monitor Endpoint dialog ── */}
      {showEndpointForm && (
        <div className="ac-overlay" onClick={e => { if (e.target === e.currentTarget) { setEditEndpointId(null); setIsCloneMode(false); setShowEndpointForm(false); } }}>
          <div className="ac-dialog" style={{ maxWidth: "540px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">{editEndpointId ? "Edit Monitoring Endpoint" : isCloneMode ? "Clone Monitoring Endpoint" : "Add Monitoring Endpoint"}</h2>
              <button type="button" className="ac-dialog__close" onClick={() => { setEditEndpointId(null); setIsCloneMode(false); setShowEndpointForm(false); }}>&#10005;</button>
            </div>
            <form onSubmit={addEndpoint} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div className="ac-dialog__body" style={{ overflowY: "auto", flex: 1 }}>
                {error && <div className="ac-alert ac-alert--error">{error}</div>}

                <div className="ac-field">
                  <label>Service / Component / Region *</label>
                  {serviceRegions.length === 0 ? (
                    <p style={{ fontSize: "0.8125rem", color: "var(--aptean-color-error, #a4262c)", marginTop: "0.25rem" }}>
                      No component-region pairs found. Go to <strong>Services</strong>, expand a service, add components, and link them to regions first.
                    </p>
                  ) : (
                    <select className="aptean-input" value={epServiceRegion} onChange={e => setEpServiceRegion(e.target.value)} required>
                      <option value="">— select —</option>
                      {[...serviceRegions].sort((a, b) => a.service_name.localeCompare(b.service_name) || a.label.localeCompare(b.label)).map(sr => (
                        <option key={sr.id} value={sr.id}>{sr.service_name} / {sr.label}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="ac-field">
                  <label>Monitor Type *</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.25rem" }}>
                    {([
                      ["http",     "Healthcheck Endpoint"],
                      ["aks_pod",  "AKS Pod Status"],
                      ["both",     "Healthcheck Endpoint and AKS Pod Status"],
                    ] as [typeof epMonitorMode, string][]).map(([v, label]) => (
                      <label key={v} className="ac-checkbox-item" style={{ padding: "0.625rem 0.875rem", border: `1px solid ${epMonitorMode === v ? "var(--aptean-teal, #54B3BE)" : "var(--aptean-border-default)"}`, borderRadius: "6px", cursor: "pointer" }}>
                        <input type="radio" name="monitorMode" value={v} checked={epMonitorMode === v} onChange={() => setEpMonitorMode(v)} />
                        {label}
                      </label>
                    ))}
                  </div>
                  {editEndpointId && epMonitorMode === "both" && (
                    <p style={{ fontSize: "0.75rem", color: "var(--aptean-warning, #f7a800)", marginTop: "0.4rem" }}>
                      Edit mode: the existing <strong>{epMonitorType === "aks_pod" ? "AKS Pod Status" : "Healthcheck"}</strong> monitor will be updated and a new <strong>{epMonitorType === "aks_pod" ? "Healthcheck" : "AKS Pod Status"}</strong> monitor will be created for the same service region.
                    </p>
                  )}
                </div>

                {(epMonitorMode === "http" || epMonitorMode === "both") && (
                  <>
                    {epMonitorMode === "both" && (
                      <p style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--aptean-teal, #54B3BE)", margin: "0.75rem 0 0.25rem" }}>
                        Healthcheck Endpoint
                      </p>
                    )}
                    <div className="ac-field">
                      <label>Endpoint URL *</label>
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                        <input type="url" className="aptean-input" style={{ flex: 1 }} value={epUrl} onChange={e => { setEpUrl(e.target.value); setProbeResult(null); }} placeholder="https://api.example.com/health" required />
                        <button
                          type="button"
                          className="aptean-btn aptean-btn--ghost"
                          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                          disabled={!epUrl.trim() || probing}
                          onClick={triggerProbe}
                        >
                          {probing ? "Checking…" : "Check"}
                        </button>
                      </div>
                      {probeResult && (
                        <div style={{ marginTop: "0.5rem", borderRadius: "6px", fontSize: "0.8125rem", border: `1px solid ${probeResult.ok ? "#107c10" : "#a4262c"}`, overflow: "hidden" }}>
                          {/* Status row */}
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.75rem", background: probeResult.status_ok ? "rgba(16,124,16,0.12)" : "rgba(164,38,44,0.12)", color: probeResult.status_ok ? "#107c10" : "#a4262c" }}>
                            {probeResult.status_ok
                              ? <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="8" fill="#107c10"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              : <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="8" fill="#a4262c"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="white" strokeWidth="1.75" strokeLinecap="round"/></svg>
                            }
                            {probeResult.error
                              ? probeResult.error
                              : <>Status: <strong>HTTP {probeResult.http_status}</strong>{!probeResult.status_ok && ` (expected ${epExpectedStatus})`} &mdash; {probeResult.response_ms}ms</>
                            }
                          </div>
                          {/* Body check row — only if user set an expected body */}
                          {epExpectedBody.trim() && probeResult.body_ok !== null && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.75rem", borderTop: "1px solid var(--aptean-border-default)", background: probeResult.body_ok ? "rgba(16,124,16,0.08)" : "rgba(164,38,44,0.08)", color: probeResult.body_ok ? "#107c10" : "#a4262c" }}>
                              {probeResult.body_ok
                                ? <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="8" fill="#107c10"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                : <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="8" fill="#a4262c"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="white" strokeWidth="1.75" strokeLinecap="round"/></svg>
                              }
                              Body: {probeResult.body_ok ? <>contains <strong>"{epExpectedBody.trim()}"</strong></> : <>does not contain <strong>"{epExpectedBody.trim()}"</strong></>}
                            </div>
                          )}
                          {/* JSON path check row */}
                          {epExpectedJsonPath.trim() && probeResult.json_ok !== null && (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", padding: "0.4rem 0.75rem", borderTop: "1px solid var(--aptean-border-default)", background: probeResult.json_ok ? "rgba(16,124,16,0.08)" : "rgba(164,38,44,0.08)", color: probeResult.json_ok ? "#107c10" : "#a4262c" }}>
                              {probeResult.json_ok
                                ? <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: "1px" }}><circle cx="8" cy="8" r="8" fill="#107c10"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                : <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: "1px" }}><circle cx="8" cy="8" r="8" fill="#a4262c"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="white" strokeWidth="1.75" strokeLinecap="round"/></svg>
                              }
                              <span>
                                {probeResult.json_error
                                  ? probeResult.json_error
                                  : probeResult.json_ok
                                    ? <><code style={{ fontSize: "0.75rem" }}>{epExpectedJsonPath.trim()}</code> = <strong>"{probeResult.json_actual}"</strong> &mdash; matches</>
                                    : <><code style={{ fontSize: "0.75rem" }}>{epExpectedJsonPath.trim()}</code> = <strong>"{probeResult.json_actual}"</strong> &mdash; expected <strong>"{epExpectedJsonValue.trim()}"</strong></>
                                }
                              </span>
                            </div>
                          )}
                          {/* Response body snippet */}
                          {probeResult.body_snippet !== null && (
                            <div style={{ padding: "0.4rem 0.75rem", borderTop: "1px solid var(--aptean-border-default)", background: "var(--aptean-bg-overlay, rgba(0,0,0,0.15))" }}>
                              <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)", display: "block", marginBottom: "0.2rem" }}>Response body</span>
                              <code style={{ fontSize: "0.75rem", color: "var(--aptean-text-primary)", whiteSpace: "pre-wrap", wordBreak: "break-all", display: "block", maxHeight: "100px", overflowY: "auto" }}>{probeResult.body_snippet}</code>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="ac-field-row">
                      <div className="ac-field">
                        <label>HTTP Method</label>
                        <select className="aptean-input" value={epMethod} onChange={e => setEpMethod(e.target.value)}>
                          {METHOD_OPTIONS.map(m => <option key={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="ac-field">
                        <label>Expected Status</label>
                        <input type="number" className="aptean-input" value={epExpectedStatus} onChange={e => setEpExpectedStatus(Number(e.target.value))} min={100} max={599} />
                      </div>
                    </div>
                    <div className="ac-field">
                      <label>Expected Response Contains <span style={{ fontWeight: 400 }}>(optional)</span></label>
                      <input
                        type="text"
                        className="aptean-input"
                        value={epExpectedBody}
                        onChange={e => { setEpExpectedBody(e.target.value); setProbeResult(null); }}
                        placeholder='"status":"OK"  or  healthy'
                      />
                      <p style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)", marginTop: "0.25rem" }}>
                        Response body must contain this substring to be considered healthy.
                      </p>
                    </div>

                    <div className="ac-field">
                      <label>JSON Path Check <span style={{ fontWeight: 400 }}>(optional)</span></label>
                      <p style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)", margin: "0.2rem 0 0.5rem" }}>
                        Navigate into a JSON response by dot-separated key path and assert a value — useful when the response contains nested service statuses.
                      </p>
                      <div className="ac-field-row" style={{ gap: "0.5rem" }}>
                        <div className="ac-field" style={{ flex: 2 }}>
                          <label style={{ fontSize: "0.75rem" }}>Path</label>
                          <input
                            type="text"
                            className="aptean-input"
                            value={epExpectedJsonPath}
                            onChange={e => { setEpExpectedJsonPath(e.target.value); setProbeResult(null); }}
                            placeholder="status  or  entries.My Service.status"
                          />
                        </div>
                        <div className="ac-field" style={{ flex: 1 }}>
                          <label style={{ fontSize: "0.75rem" }}>Expected Value</label>
                          <input
                            type="text"
                            className="aptean-input"
                            value={epExpectedJsonValue}
                            onChange={e => { setEpExpectedJsonValue(e.target.value); setProbeResult(null); }}
                            placeholder="Healthy"
                          />
                        </div>
                      </div>
                      {epExpectedJsonPath.trim() && (
                        <p style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)", marginTop: "0.25rem" }}>
                          Example for the response above: path <code style={{ fontSize: "0.75rem" }}>entries.m2M Web Service - Reports.status</code> → expected value <code style={{ fontSize: "0.75rem" }}>Healthy</code>
                        </p>
                      )}
                    </div>
                  </>
                )}

                {(epMonitorMode === "aks_pod" || epMonitorMode === "both") && (
                  <>
                    {epMonitorMode === "both" && (
                      <p style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--aptean-teal, #54B3BE)", margin: "0.75rem 0 0.25rem" }}>
                        AKS Pod Status
                      </p>
                    )}
                    <div className="ac-field">
                      <label>Subscription</label>
                      <select className="aptean-input" value={epAksSubFilter} onChange={e => { setEpAksSubFilter(e.target.value); setEpAksClusterId(""); setAksProbeResult(null); }}>
                        <option value="">— all subscriptions —</option>
                        {subscriptions.map(s => <option key={s.id} value={s.id}>{s.name}{s.is_gov ? " (Gov)" : ""}</option>)}
                      </select>
                    </div>
                    <div className="ac-field">
                      <label>AKS Cluster *</label>
                      <select className="aptean-input" value={epAksClusterId} onChange={e => { setEpAksClusterId(e.target.value); setAksProbeResult(null); }} required>
                        <option value="">— select cluster —</option>
                        {aksClusters
                          .filter(c => !epAksSubFilter || c.subscription === epAksSubFilter)
                          .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      {aksClusters.length === 0 && <span style={{ fontSize: "0.75rem", color: "var(--aptean-warning, #f7a800)" }}>No clusters configured. Add one in the AKS Clusters tab first.</span>}
                    </div>
                    <div className="ac-field">
                      <label>Namespace *</label>
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                        <input
                          type="text"
                          className="aptean-input"
                          style={{ flex: 1 }}
                          value={epAksNamespace}
                          onChange={e => { setEpAksNamespace(e.target.value); setAksProbeResult(null); }}
                          placeholder="default or ns1,ns2,ns3"
                          required
                        />
                        <button
                          type="button"
                          className="aptean-btn aptean-btn--ghost"
                          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                          disabled={!epAksClusterId || aksProbing}
                          onClick={triggerAksProbe}
                        >
                          {aksProbing ? "Checking…" : "Check"}
                        </button>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
                        Comma-separated namespaces (e.g. <code>ns1,ns2</code>). All pods in each namespace are checked. Leave blank to use <code>default</code>.
                      </span>
                      {aksProbeResult && (
                        <div style={{ marginTop: "0.5rem", borderRadius: "6px", fontSize: "0.8125rem", border: `1px solid ${aksProbeResult.ok ? "#107c10" : aksProbeResult.status === "degraded" ? "#f7a800" : "#a4262c"}`, overflow: "hidden" }}>
                          {/* Status row */}
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.75rem", background: aksProbeResult.ok ? "rgba(16,124,16,0.12)" : aksProbeResult.status === "degraded" ? "rgba(247,168,0,0.12)" : "rgba(164,38,44,0.12)", color: aksProbeResult.ok ? "#107c10" : aksProbeResult.status === "degraded" ? "#b07800" : "#a4262c" }}>
                            {aksProbeResult.ok
                              ? <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="8" fill="#107c10"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              : aksProbeResult.status === "degraded"
                                ? <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="8" fill="#f7a800"/><path d="M8 5v4M8 11v1" stroke="white" strokeWidth="1.75" strokeLinecap="round"/></svg>
                                : <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="8" fill="#a4262c"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="white" strokeWidth="1.75" strokeLinecap="round"/></svg>
                            }
                            <span>
                              {aksProbeResult.error
                                ? aksProbeResult.error
                                : <><strong style={{ textTransform: "capitalize" }}>{aksProbeResult.status}</strong> — {aksProbeResult.detail || `${aksProbeResult.pod_ready ?? 0}/${aksProbeResult.pod_total ?? 0} pods ready`}</>
                              }
                            </span>
                          </div>
                          {/* Detail row */}
                          {aksProbeResult.detail && !aksProbeResult.error && (
                            <div style={{ padding: "0.4rem 0.75rem", borderTop: "1px solid var(--aptean-border-default)", background: "var(--aptean-bg-overlay, rgba(0,0,0,0.15))", fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
                              {aksProbeResult.detail}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="ac-field-row">
                  <div className="ac-field">
                    <label>Poll Interval (seconds)</label>
                    <input type="number" className="aptean-input" value={epInterval} onChange={e => setEpInterval(Number(e.target.value))} min={10} />
                  </div>
                  <div className="ac-field">
                    <label>Timeout (seconds)</label>
                    <input type="number" className="aptean-input" value={epTimeout} onChange={e => setEpTimeout(Number(e.target.value))} min={1} />
                  </div>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => { setEditEndpointId(null); setIsCloneMode(false); setShowEndpointForm(false); }}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={!editEndpointId && serviceRegions.length === 0}>{editEndpointId ? "Save Changes" : isCloneMode ? "Save as New" : "Add Monitor"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ── Add / Edit Subscription dialog ── */}
      {showSubForm && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowSubForm(false)}>
          <div className="ac-dialog" style={{ maxWidth: "520px" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">{editSubId ? "Edit Subscription" : "Add Subscription"}</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowSubForm(false)}>&#10005;</button>
            </div>
            <form onSubmit={saveSub}>
              <div className="ac-dialog__body">
                {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
                <div className="ac-field">
                  <label>Name *</label>
                  <input type="text" className="aptean-input" value={subName} onChange={e => setSubName(e.target.value)} placeholder="e.g. ApteanONE-RnD" required />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>Unique identifier for this subscription.</span>
                </div>
                <div className="ac-field">
                  <label>Subscription ID *</label>
                  <input type="text" className="aptean-input" value={subSubscriptionId} onChange={e => setSubSubscriptionId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
                </div>
                <div className="ac-field">
                  <label>Tenant ID *</label>
                  <input type="text" className="aptean-input" value={subTenantId} onChange={e => setSubTenantId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
                </div>
                <div className="ac-field">
                  <label>Client ID *</label>
                  <input type="text" className="aptean-input" value={subClientId} onChange={e => setSubClientId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
                </div>
                <div className="ac-field">
                  <label>Client Secret {editSubId ? <span style={{ fontWeight: 400 }}>(leave blank to keep existing)</span> : "*"}</label>
                  <input type="password" className="aptean-input" value={subClientSecret} onChange={e => setSubClientSecret(e.target.value)} placeholder={editSubId ? "Leave blank to keep existing" : "Enter client secret"} required={!editSubId} />
                </div>
                <div className="ac-field">
                  <label>Secret Expiry Date</label>
                  <input type="date" className="aptean-input" value={subSecretExpiresAt} onChange={e => setSubSecretExpiresAt(e.target.value)} />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>Optional. Shows a warning badge when expiry is within 30 days.</span>
                </div>
                <div className="ac-field">
                  <label className="ac-checkbox-item" style={{ padding: "0.5rem 0.75rem", border: "1px solid var(--aptean-border-default)", borderRadius: "6px", cursor: "pointer" }}>
                    <input type="checkbox" checked={subIsGov} onChange={e => setSubIsGov(e.target.checked)} style={{ marginRight: "0.5rem" }} />
                    Azure US Government tenant
                  </label>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowSubForm(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary">{editSubId ? "Save Changes" : "Add Subscription"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add / Edit AKS Cluster dialog ── */}
      {showClusterForm && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowClusterForm(false)}>
          <div className="ac-dialog" style={{ maxWidth: "420px" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">{editClusterId ? "Edit AKS Cluster" : "Add AKS Cluster"}</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowClusterForm(false)}>&#10005;</button>
            </div>
            <form onSubmit={saveCluster}>
              <div className="ac-dialog__body">
                {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
                <div className="ac-field">
                  <label>Subscription *</label>
                  <select className="aptean-input" value={clusterSubId} onChange={e => setClusterSubId(e.target.value)} required>
                    <option value="">— select —</option>
                    {subscriptions.map(s => <option key={s.id} value={s.id}>{s.name}{s.is_gov ? " (Gov)" : ""}</option>)}
                  </select>
                </div>
                <div className="ac-field">
                  <label>Cluster Name *</label>
                  <input type="text" className="aptean-input" value={clusterName} onChange={e => setClusterName(e.target.value)} placeholder="e.g. my-aks-cluster" required />
                  <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>Azure resource name of the managed cluster.</span>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowClusterForm(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary">{editClusterId ? "Save Changes" : "Add Cluster"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
